import { Box, Divider, List, ListItem, ListItemIcon, ListItemText, Paper } from "@material-ui/core"
import CachedIcon from "@material-ui/icons/Cached"
import LandscapeIcon from "@material-ui/icons/Landscape"
import MapIcon from "@material-ui/icons/Map"
import MenuBookIcon from "@material-ui/icons/MenuBook"
import PublicIcon from "@material-ui/icons/Public"
import TuneIcon from "@material-ui/icons/Tune"
import VerifiedUserIcon from "@material-ui/icons/VerifiedUser"
import { useState } from "react"
import type { ReactElement } from "react"
import { NavLink, useRouteMatch } from "react-router-dom"
import BackupIcon from "@material-ui/icons/Backup"

import { ContentManagerPageRouting } from ".."
import { ConfirmationDialog, LicenceSwitcher, PageContainer } from "../../molecules"
import type { IConfirmationDialogProps } from "../../molecules"

import "./content-manager-page.styles.scss"
import MyLocation from "@material-ui/icons/MyLocation"
import { useTranslation } from "react-i18next"
import { connect } from "react-redux"
import { deleteSearchCache } from "../../../redux"
import { UploadTopographyDataDialog } from "../../organisms"
import type { IUploadTopograpyDataDialogProps } from "../../organisms"
import type { AppThunkDispatch, SearchActions } from "../../../redux"

export interface IContentManagerPageProps {
  clearSearchCache: () => void
}

export function ContentManagerPageView(props: IContentManagerPageProps): ReactElement {
  const { clearSearchCache } = props
  const { url } = useRouteMatch()
  const { t } = useTranslation("contentManager")

  const [uploadTopoDataDialogProps, setUploadTopoDataDialogProps] = useState<IUploadTopograpyDataDialogProps>({
    open: false,
    onClose: closeTopographyDialog
  })

  const [confirmationDialogProps, setConfirmationDialogProps] = useState<IConfirmationDialogProps>({
    onCancel: closeConfirmationDialog,
    onConfirm: handleClearSearchCache,
    open: false,
    title: t("dialog.delete.deleteSearchCache"),
    confirmText: t("dialog.delete.deleteText"),
    bodyText: t("dialog.delete.confirmDeleteSearchCache")
  })

  function closeTopographyDialog(): void {
    setUploadTopoDataDialogProps({
      ...uploadTopoDataDialogProps,
      open: false
    })
  }

  function openTopographyDialog(): void {
    setUploadTopoDataDialogProps({
      ...uploadTopoDataDialogProps,
      open: true
    })
  }

  function openConfirmationDialog(): void {
    setConfirmationDialogProps({
      ...confirmationDialogProps,
      open: true
    })
  }

  function closeConfirmationDialog(): void {
    setConfirmationDialogProps({
      ...confirmationDialogProps,
      open: false
    })
  }

  function handleClearSearchCache(): void {
    clearSearchCache()
    closeConfirmationDialog()
  }

  return (
    <PageContainer id="content-manager-page" noPadding>
      <Paper className="content-manager-navigation" elevation={3}>
        <List>
          <ListItem component={NavLink} button to={`${url}/assets`} activeClassName="Mui-selected">
            <ListItemIcon>
              <MapIcon />
            </ListItemIcon>
            <ListItemText primary={t("assets.title")} />
          </ListItem>
          <ListItem component={NavLink} button to={`${url}/metadata`} activeClassName="Mui-selected">
            <ListItemIcon>
              <TuneIcon />
            </ListItemIcon>
            <ListItemText primary={t("metadata.title")} />
          </ListItem>
          <ListItem component={NavLink} button to={`${url}/chapters`} activeClassName="Mui-selected">
            <ListItemIcon>
              <MenuBookIcon />
            </ListItemIcon>
            <ListItemText primary={t("chapters.title")} />
          </ListItem>
          <ListItem component={NavLink} button to={`${url}/linkchapters`} activeClassName="Mui-selected">
            <ListItemIcon>
              <MenuBookIcon />
            </ListItemIcon>
            <ListItemText primary={t("linkChapters.title")} />
          </ListItem>
          <ListItem component={NavLink} button to={`${url}/regions`} activeClassName="Mui-selected">
            <ListItemIcon>
              <PublicIcon />
            </ListItemIcon>
            <ListItemText primary={t("regions.title")} />
          </ListItem>
          <ListItem component={NavLink} button to={`${url}/themes`} activeClassName="Mui-selected">
            <ListItemIcon>
              <LandscapeIcon />
            </ListItemIcon>
            <ListItemText primary={t("themes.title")} />
          </ListItem>
          <ListItem component={NavLink} button to={`${url}/licences`} activeClassName="Mui-selected">
            <ListItemIcon>
              <VerifiedUserIcon />
            </ListItemIcon>
            <ListItemText primary={t("licences.title")} />
          </ListItem>
          <ListItem component={NavLink} button to={`${url}/topography`} activeClassName="Mui-selected">
            <ListItemIcon>
              <MyLocation />
            </ListItemIcon>
            <ListItemText primary={t("topography.title")} />
          </ListItem>
          <Divider />
          <ListItem button onClick={openTopographyDialog}>
            <ListItemIcon>
              <BackupIcon />
            </ListItemIcon>
            <ListItemText primary={t("uploadTopography.title")} />
          </ListItem>
          <ListItem button onClick={openConfirmationDialog}>
            <ListItemIcon>
              <CachedIcon />
            </ListItemIcon>
            <ListItemText primary={t("deleteCache")} />
          </ListItem>
          <ListItem>
            <LicenceSwitcher />
          </ListItem>
        </List>
      </Paper>
      <Box className="content-manager-container">
        <ContentManagerPageRouting />
      </Box>
      <UploadTopographyDataDialog {...uploadTopoDataDialogProps} />
      <ConfirmationDialog {...confirmationDialogProps} />
    </PageContainer>
  )
}

function mapDispatchToProps(dispatch: AppThunkDispatch<SearchActions>) {
  return {
    clearSearchCache: () => dispatch(deleteSearchCache())
  }
}

export const ContentManagerPage = connect(undefined, mapDispatchToProps)(ContentManagerPageView)
